/**
 * MobileControlsSettings - Settings modal for mobile control customization
 *
 * Provides UI for adjusting:
 * - Control mode (Joystick / Pointer)
 * - Handedness (Left / Right)
 * - Joystick size (Small / Medium / Large)
 * - Button size (Small / Medium / Large)
 * - Joystick opacity
 * - Vibration toggle
 */

import { SoundManager, SoundType } from '../audio/SoundManager';
import {
  MobileSettings,
  MobileSettingsConfig,
  ControlMode,
  Handedness,
  ControlSize,
} from '../utils/MobileSettings';

/**
 * Event handlers for MobileControlsSettings
 */
export interface MobileControlsSettingsEvents {
  onClose: () => void;
  onSettingsChange?: (settings: MobileSettingsConfig) => void;
}

/**
 * MobileControlsSettings - Modal for customizing mobile controls
 */
export class MobileControlsSettings {
  private container: HTMLDivElement;
  private content: HTMLDivElement;
  private events: MobileControlsSettingsEvents;
  private visible: boolean = false;
  private keydownListener: ((e: KeyboardEvent) => void) | null = null;
  private mobileSettings: MobileSettings;

  // UI elements that need updating
  private opacitySlider: HTMLInputElement | null = null;
  private opacityValue: HTMLSpanElement | null = null;
  private previewJoystick: HTMLDivElement | null = null;

  constructor(events: MobileControlsSettingsEvents) {
    this.events = events;
    this.mobileSettings = MobileSettings.getInstance();

    this.container = document.createElement('div');
    this.container.id = 'mobile-controls-settings';
    this.container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: none;
      justify-content: center;
      align-items: center;
      background: rgba(10, 10, 20, 0.9);
      backdrop-filter: blur(6px);
      z-index: 950;
      font-family: 'Segoe UI', Arial, sans-serif;
      opacity: 0;
      transition: opacity 0.2s ease-out;
      overflow-y: auto;
      padding: 20px;
    `;

    this.content = document.createElement('div');
    this.content.style.cssText = `
      width: 100%;
      max-width: 420px;
      max-height: 90vh;
      overflow-y: auto;
      padding: 30px 35px;
      background: linear-gradient(145deg, rgba(40, 40, 70, 0.98), rgba(25, 25, 45, 0.99));
      border-radius: 20px;
      border: 2px solid rgba(78, 205, 196, 0.3);
      box-shadow: 0 15px 50px rgba(0, 0, 0, 0.5), 0 0 40px rgba(78, 205, 196, 0.1);
      transform: scale(0.9) translateY(-20px);
      transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
    `;

    this.buildUI();
    this.container.appendChild(this.content);
    document.body.appendChild(this.container);

    // Close when clicking outside
    this.container.addEventListener('click', (e) => {
      if (e.target === this.container) {
        this.close();
      }
    });
  }

  /**
   * Build the settings UI
   */
  private buildUI(): void {
    // Header
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 25px;
    `;

    const title = document.createElement('h2');
    title.innerHTML = '&#x1F3AE; Mobile Controls';
    title.style.cssText = `
      font-size: 24px;
      font-weight: 800;
      margin: 0;
      color: #4ecdc4;
      text-shadow: 0 2px 10px rgba(78, 205, 196, 0.4);
    `;
    header.appendChild(title);

    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '&times;';
    closeBtn.style.cssText = `
      background: none;
      border: none;
      font-size: 32px;
      color: #888;
      cursor: pointer;
      padding: 0;
      line-height: 1;
      transition: color 0.2s;
    `;
    closeBtn.addEventListener('click', () => this.close());
    closeBtn.addEventListener('mouseover', () => {
      closeBtn.style.color = '#fff';
    });
    closeBtn.addEventListener('mouseout', () => {
      closeBtn.style.color = '#888';
    });
    header.appendChild(closeBtn);

    this.content.appendChild(header);

    // Preview section
    this.content.appendChild(this.createPreviewSection());

    // Control Mode
    this.content.appendChild(this.createSection(
      'Control Mode',
      'How you control direction',
      this.createToggleGroup([
        { value: ControlMode.JOYSTICK, label: 'Joystick', icon: '&#x1F579;' },
        { value: ControlMode.POINTER, label: 'Follow Finger', icon: '&#x1F446;' },
      ], this.mobileSettings.controlMode, (value) => {
        this.mobileSettings.controlMode = value as ControlMode;
        this.notifyChange();
      })
    ));

    // Handedness
    this.content.appendChild(this.createSection(
      'Handedness',
      'Which side for the joystick',
      this.createToggleGroup([
        { value: Handedness.RIGHT, label: 'Right-Handed', icon: '&#x1F449;' },
        { value: Handedness.LEFT, label: 'Left-Handed', icon: '&#x1F448;' },
      ], this.mobileSettings.handedness, (value) => {
        this.mobileSettings.handedness = value as Handedness;
        this.notifyChange();
      })
    ));

    // Joystick Size
    this.content.appendChild(this.createSection(
      'Joystick Size',
      '',
      this.createSizeSelector('joystick', this.mobileSettings.joystickSize, (value) => {
        this.mobileSettings.joystickSize = value;
        this.updatePreview();
        this.notifyChange();
      })
    ));

    // Button Size
    this.content.appendChild(this.createSection(
      'Button Size',
      '',
      this.createSizeSelector('button', this.mobileSettings.buttonSize, (value) => {
        this.mobileSettings.buttonSize = value;
        this.notifyChange();
      })
    ));

    // Joystick Opacity
    this.content.appendChild(this.createSection(
      'Joystick Opacity',
      '',
      this.createOpacitySlider()
    ));

    // Vibration Toggle
    this.content.appendChild(this.createSection(
      'Haptic Feedback',
      '',
      this.createVibrationToggle()
    ));

    // Reset button
    const resetBtn = document.createElement('button');
    resetBtn.textContent = 'Reset to Defaults';
    resetBtn.style.cssText = `
      width: 100%;
      margin-top: 20px;
      padding: 12px 20px;
      font-size: 14px;
      font-weight: 600;
      border: 2px solid rgba(150, 150, 170, 0.3);
      border-radius: 10px;
      background: rgba(100, 100, 120, 0.1);
      color: #999;
      cursor: pointer;
      transition: all 0.2s ease;
    `;
    resetBtn.addEventListener('click', () => {
      SoundManager.getInstance().play(SoundType.UI_CLICK);
      this.mobileSettings.reset();
      this.refreshUI();
      this.updatePreview();
      this.notifyChange();
    });
    resetBtn.addEventListener('mouseover', () => {
      SoundManager.getInstance().play(SoundType.UI_HOVER);
      resetBtn.style.borderColor = 'rgba(150, 150, 170, 0.5)';
      resetBtn.style.color = '#bbb';
    });
    resetBtn.addEventListener('mouseout', () => {
      resetBtn.style.borderColor = 'rgba(150, 150, 170, 0.3)';
      resetBtn.style.color = '#999';
    });
    this.content.appendChild(resetBtn);
  }

  /**
   * Create preview section showing joystick visualization
   */
  private createPreviewSection(): HTMLDivElement {
    const section = document.createElement('div');
    section.style.cssText = `
      background: rgba(26, 26, 46, 0.6);
      border-radius: 12px;
      padding: 15px;
      margin-bottom: 20px;
      display: flex;
      justify-content: center;
      align-items: center;
    `;

    this.previewJoystick = document.createElement('div');
    this.updatePreview();
    section.appendChild(this.previewJoystick);

    return section;
  }

  /**
   * Update the joystick preview based on current settings
   */
  private updatePreview(): void {
    if (!this.previewJoystick) return;

    const sizes = this.mobileSettings.getJoystickPixelSizes();
    const opacity = this.mobileSettings.joystickOpacity;

    // Scale down for preview (0.6x)
    const scale = 0.6;
    const outerSize = Math.round(sizes.outer * scale);
    const innerSize = Math.round(sizes.inner * scale);

    this.previewJoystick.style.cssText = `
      width: ${outerSize}px;
      height: ${outerSize}px;
      border-radius: 50%;
      background: radial-gradient(circle at center,
        rgba(78, 205, 196, ${0.15 * opacity}) 0%,
        rgba(78, 205, 196, ${0.05 * opacity}) 100%);
      border: 3px solid rgba(78, 205, 196, ${0.4 * opacity});
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
    `;

    // Inner handle
    this.previewJoystick.innerHTML = `
      <div style="
        width: ${innerSize}px;
        height: ${innerSize}px;
        border-radius: 50%;
        background: radial-gradient(circle at 30% 30%,
          rgba(78, 205, 196, ${0.9 * opacity}),
          rgba(58, 168, 158, ${0.8 * opacity}));
        box-shadow: 0 4px 15px rgba(0, 0, 0, ${0.3 * opacity}),
                    0 0 20px rgba(78, 205, 196, ${0.3 * opacity});
      "></div>
    `;
  }

  /**
   * Create a settings section
   */
  private createSection(title: string, subtitle: string, content: HTMLElement): HTMLDivElement {
    const section = document.createElement('div');
    section.style.cssText = `
      margin-bottom: 18px;
    `;

    const titleEl = document.createElement('div');
    titleEl.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
    `;

    const titleText = document.createElement('span');
    titleText.textContent = title;
    titleText.style.cssText = `
      font-size: 14px;
      font-weight: 600;
      color: #ddd;
    `;
    titleEl.appendChild(titleText);

    if (subtitle) {
      const subtitleEl = document.createElement('span');
      subtitleEl.textContent = subtitle;
      subtitleEl.style.cssText = `
        font-size: 11px;
        color: #777;
      `;
      titleEl.appendChild(subtitleEl);
    }

    section.appendChild(titleEl);
    section.appendChild(content);

    return section;
  }

  /**
   * Create toggle group (2 options)
   */
  private createToggleGroup(
    options: { value: string; label: string; icon: string }[],
    currentValue: string,
    onChange: (value: string) => void
  ): HTMLDivElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      gap: 10px;
    `;

    const buttons: HTMLButtonElement[] = [];

    options.forEach((opt) => {
      const btn = document.createElement('button');
      btn.dataset.value = opt.value;
      const isActive = opt.value === currentValue;

      btn.innerHTML = `<span style="margin-right: 6px;">${opt.icon}</span>${opt.label}`;
      btn.style.cssText = `
        flex: 1;
        padding: 12px 16px;
        font-size: 13px;
        font-weight: 600;
        border: 2px solid ${isActive ? 'rgba(78, 205, 196, 0.5)' : 'rgba(100, 100, 120, 0.3)'};
        border-radius: 10px;
        background: ${isActive ? 'rgba(78, 205, 196, 0.15)' : 'rgba(50, 50, 70, 0.3)'};
        color: ${isActive ? '#4ecdc4' : '#999'};
        cursor: pointer;
        transition: all 0.2s ease;
      `;

      btn.addEventListener('click', () => {
        SoundManager.getInstance().play(SoundType.UI_CLICK);
        buttons.forEach((b) => {
          const active = b === btn;
          b.style.borderColor = active ? 'rgba(78, 205, 196, 0.5)' : 'rgba(100, 100, 120, 0.3)';
          b.style.background = active ? 'rgba(78, 205, 196, 0.15)' : 'rgba(50, 50, 70, 0.3)';
          b.style.color = active ? '#4ecdc4' : '#999';
        });
        onChange(opt.value);
      });

      btn.addEventListener('mouseover', () => {
        SoundManager.getInstance().play(SoundType.UI_HOVER);
      });

      buttons.push(btn);
      container.appendChild(btn);
    });

    // Store reference for refresh
    container.dataset.type = 'toggleGroup';
    container.dataset.currentValue = currentValue;

    return container;
  }

  /**
   * Create size selector (Small / Medium / Large)
   */
  private createSizeSelector(
    type: 'joystick' | 'button',
    currentValue: ControlSize,
    onChange: (value: ControlSize) => void
  ): HTMLDivElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      gap: 8px;
    `;

    const sizes: { value: ControlSize; label: string }[] = [
      { value: ControlSize.SMALL, label: 'S' },
      { value: ControlSize.MEDIUM, label: 'M' },
      { value: ControlSize.LARGE, label: 'L' },
    ];

    const buttons: HTMLButtonElement[] = [];

    sizes.forEach((size) => {
      const btn = document.createElement('button');
      btn.dataset.value = size.value;
      const isActive = size.value === currentValue;

      btn.textContent = size.label;
      btn.style.cssText = `
        flex: 1;
        padding: 10px 16px;
        font-size: 14px;
        font-weight: 700;
        border: 2px solid ${isActive ? 'rgba(78, 205, 196, 0.5)' : 'rgba(100, 100, 120, 0.3)'};
        border-radius: 8px;
        background: ${isActive ? 'rgba(78, 205, 196, 0.15)' : 'rgba(50, 50, 70, 0.3)'};
        color: ${isActive ? '#4ecdc4' : '#888'};
        cursor: pointer;
        transition: all 0.2s ease;
      `;

      btn.addEventListener('click', () => {
        SoundManager.getInstance().play(SoundType.UI_CLICK);
        buttons.forEach((b) => {
          const active = b === btn;
          b.style.borderColor = active ? 'rgba(78, 205, 196, 0.5)' : 'rgba(100, 100, 120, 0.3)';
          b.style.background = active ? 'rgba(78, 205, 196, 0.15)' : 'rgba(50, 50, 70, 0.3)';
          b.style.color = active ? '#4ecdc4' : '#888';
        });
        onChange(size.value);
      });

      btn.addEventListener('mouseover', () => {
        SoundManager.getInstance().play(SoundType.UI_HOVER);
      });

      buttons.push(btn);
      container.appendChild(btn);
    });

    container.dataset.type = type + 'Size';
    container.dataset.currentValue = currentValue;

    return container;
  }

  /**
   * Create opacity slider
   */
  private createOpacitySlider(): HTMLDivElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      align-items: center;
      gap: 12px;
    `;

    this.opacitySlider = document.createElement('input');
    this.opacitySlider.type = 'range';
    this.opacitySlider.min = '30';
    this.opacitySlider.max = '100';
    this.opacitySlider.value = String(Math.round(this.mobileSettings.joystickOpacity * 100));
    this.opacitySlider.style.cssText = `
      flex: 1;
      height: 8px;
      border-radius: 4px;
      background: rgba(100, 100, 120, 0.3);
      outline: none;
      appearance: none;
      -webkit-appearance: none;
      cursor: pointer;
    `;

    // Style the slider thumb
    const sliderStyle = document.createElement('style');
    sliderStyle.textContent = `
      #mobile-controls-settings input[type="range"]::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: linear-gradient(135deg, #4ecdc4 0%, #3aa89e 100%);
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(78, 205, 196, 0.4);
        border: 2px solid rgba(255, 255, 255, 0.2);
      }
      #mobile-controls-settings input[type="range"]::-moz-range-thumb {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: linear-gradient(135deg, #4ecdc4 0%, #3aa89e 100%);
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(78, 205, 196, 0.4);
        border: 2px solid rgba(255, 255, 255, 0.2);
      }
    `;
    document.head.appendChild(sliderStyle);

    this.opacityValue = document.createElement('span');
    this.opacityValue.textContent = this.opacitySlider.value + '%';
    this.opacityValue.style.cssText = `
      min-width: 45px;
      text-align: right;
      font-size: 14px;
      font-weight: 600;
      color: #4ecdc4;
    `;

    this.opacitySlider.addEventListener('input', () => {
      const value = parseInt(this.opacitySlider!.value) / 100;
      this.mobileSettings.joystickOpacity = value;
      this.opacityValue!.textContent = this.opacitySlider!.value + '%';
      this.updatePreview();
      this.notifyChange();
    });

    container.appendChild(this.opacitySlider);
    container.appendChild(this.opacityValue);

    return container;
  }

  /**
   * Create vibration toggle
   */
  private createVibrationToggle(): HTMLDivElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: space-between;
    `;

    const labelContainer = document.createElement('div');
    labelContainer.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    const icon = document.createElement('span');
    icon.innerHTML = '&#x1F4F3;';
    icon.style.fontSize = '18px';
    labelContainer.appendChild(icon);

    const label = document.createElement('span');
    label.textContent = 'Vibration on tap';
    label.style.cssText = `
      font-size: 13px;
      color: #999;
    `;
    labelContainer.appendChild(label);

    container.appendChild(labelContainer);

    // Toggle switch
    const toggle = document.createElement('div');
    const isEnabled = this.mobileSettings.vibrationEnabled;
    toggle.style.cssText = `
      width: 50px;
      height: 28px;
      border-radius: 14px;
      background: ${isEnabled ? 'rgba(78, 205, 196, 0.5)' : 'rgba(100, 100, 120, 0.3)'};
      cursor: pointer;
      position: relative;
      transition: all 0.2s ease;
    `;

    const toggleHandle = document.createElement('div');
    toggleHandle.style.cssText = `
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: ${isEnabled ? '#4ecdc4' : '#888'};
      position: absolute;
      top: 3px;
      left: ${isEnabled ? '25px' : '3px'};
      transition: all 0.2s ease;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    `;
    toggle.appendChild(toggleHandle);

    toggle.addEventListener('click', () => {
      SoundManager.getInstance().play(SoundType.UI_CLICK);
      const newValue = !this.mobileSettings.vibrationEnabled;
      this.mobileSettings.vibrationEnabled = newValue;

      toggle.style.background = newValue ? 'rgba(78, 205, 196, 0.5)' : 'rgba(100, 100, 120, 0.3)';
      toggleHandle.style.background = newValue ? '#4ecdc4' : '#888';
      toggleHandle.style.left = newValue ? '25px' : '3px';

      // Test vibration if enabled
      if (newValue) {
        this.mobileSettings.hapticMedium();
      }

      this.notifyChange();
    });

    toggle.dataset.type = 'vibrationToggle';
    container.appendChild(toggle);

    return container;
  }

  /**
   * Refresh UI to match current settings
   */
  private refreshUI(): void {
    // Rebuild the entire UI for simplicity
    this.content.innerHTML = '';
    this.buildUI();
  }

  /**
   * Notify listeners of settings change
   */
  private notifyChange(): void {
    if (this.events.onSettingsChange) {
      this.events.onSettingsChange(this.mobileSettings.settings);
    }
  }

  /**
   * Show the modal
   */
  show(): void {
    if (this.visible) return;

    this.visible = true;
    this.container.style.display = 'flex';

    // Trigger animations
    requestAnimationFrame(() => {
      this.container.style.opacity = '1';
      this.content.style.transform = 'scale(1) translateY(0)';
    });

    // Add keyboard listener to close on ESC
    this.keydownListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      }
    };
    document.addEventListener('keydown', this.keydownListener);

    // Refresh UI to show current settings
    this.updatePreview();
  }

  /**
   * Hide the modal
   */
  hide(): void {
    if (!this.visible) return;

    this.visible = false;

    // Remove keyboard listener
    if (this.keydownListener) {
      document.removeEventListener('keydown', this.keydownListener);
      this.keydownListener = null;
    }

    // Animate out
    this.container.style.opacity = '0';
    this.content.style.transform = 'scale(0.9) translateY(-20px)';

    setTimeout(() => {
      this.container.style.display = 'none';
    }, 200);
  }

  /**
   * Close the modal and trigger callback
   */
  close(): void {
    SoundManager.getInstance().play(SoundType.UI_CLICK);
    this.hide();
    this.events.onClose();
  }

  /**
   * Check if visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Clean up
   */
  destroy(): void {
    if (this.keydownListener) {
      document.removeEventListener('keydown', this.keydownListener);
    }
    this.container.remove();
  }
}

// Singleton instance
let mobileControlsSettingsInstance: MobileControlsSettings | null = null;

/**
 * Get or create the mobile controls settings modal
 */
export function getMobileControlsSettings(events?: MobileControlsSettingsEvents): MobileControlsSettings {
  if (!mobileControlsSettingsInstance && events) {
    mobileControlsSettingsInstance = new MobileControlsSettings(events);
  }
  return mobileControlsSettingsInstance!;
}

/**
 * Destroy the mobile controls settings modal
 */
export function destroyMobileControlsSettings(): void {
  if (mobileControlsSettingsInstance) {
    mobileControlsSettingsInstance.destroy();
    mobileControlsSettingsInstance = null;
  }
}
